import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import ApiClient from '@src/lib/api_client'
import Note from '@src/data/note'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Link } from '@src/elements/link/link'
import { API } from '@src/lib/api_path'
import { NoteFilters } from '@src/state/store'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import EventBus from '@src/lib/event_bus'
import { convertLocalDateToDate } from '@src/util/date-format'
import { defaultMemberNoteFilters } from '@src/state/dafaults'
import { List } from '@src/routes/content/components/list'

import './list.sass'

type NotesResponse = { notes: Note[] }

@Component({
  computed: {
    filters () {
      return this.$store.state.memberNoteFilters
    }
  }
})
export class NoteList extends Vue {

  protected list: Note[] = []
  protected loading: boolean = true
  private apiClient: ApiClient
  private filters: NoteFilters
  private currentPage: number = 1

  constructor () {
    super()
    this.apiClient = new ApiClient(API.TRAINERS_API.MEMBERS)
  }

  @Watch('filters')
  filtersChanged () {
    return this.changePage(1)
  }

  filterNotes (list: Note[]) {
    const filterByStartDate = (note: Note) => {
      if (!this.filters.startDate) {
        return true
      }
      let startDate = convertLocalDateToDate(this.filters.startDate)
      startDate.setHours(0, 0, 0)
      return note.createdAt >= startDate
    }
    const filterByEndDate = (note: Note) => {
      if (!this.filters.endDate) {
        return true
      }
      let endDate: Date = convertLocalDateToDate(this.filters.endDate)
      endDate.setHours(23, 59, 59)
      return note.createdAt <= endDate
    }
    const filterByWord = (note: Note) => {
      let needle = new RegExp(this.filters.word || '', 'i')
      return note.text.match(needle) || (note.tags && note.tags.filter(tag => tag.match(needle)).length > 0)
    }

    let filteredNotes = list.filter((note) => note.text && filterByStartDate(note) && filterByEndDate(note) && filterByWord(note))
    // sort notes list ASC by date
    return filteredNotes.sort(function (a, b) {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return b.createdAt.getTime() - a.createdAt.getTime()
    })
  }

  resetFilters () {
    this.$store.dispatch('filterMemberNotes', defaultMemberNoteFilters())
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    if (this.list.length === 0) {
      return <ErrorScreen message={this.$t('PAGE.MEMBERS.NOTES.NO_NOTES')} />
    }

    let filteredNotes: Note[] = this.filterNotes(this.list)

    if (filteredNotes.length === 0) {
      return <ErrorScreen message={this.$t('PAGE.MEMBERS.NOTES.NOT_FOUND_GENERIC')}>
        <Link ref='resetFilters' variant='colored' align='center' click={this.resetFilters} label={this.$t('PAGE.MEMBERS.NOTES.RESET_FILTERS')} />
      </ErrorScreen>
    }
    return <List items={filteredNotes} itemType='Note' currentPage={this.currentPage}
          changePageCallback={this.changePage} styled={true}/>
  }

  changePage (newP) {
    document.getElementsByClassName('component-tabs-body')[0].scrollTop = 0
    this.currentPage = newP
  }

  beforeMount () {
    EventBus.on('reloadNotes', this.loadNotes)

    return this.loadNotes()
  }

  beforeDestroy () {
    EventBus.off('reloadNotes', this.loadNotes)
    this.resetFilters()
  }

  protected loadNotes () {
    this.loading = true
    return this.apiClient.get<NotesResponse>(`${this.$route.params.id}/notes`, {})
      .then((response) => this.list = response.notes.map(item => new Note(camelcaseKeys(item, { deep: true }))))
      .catch((error) => console.error('failed to retrieve member notes', error))
      .finally(() => this.loading = false)
  }
}
